import { computed, effect, inject } from '@angular/core';
import { ApiService, QuickSearchResponseDto } from '@core/api/angular';
import { getQuickSearchResults } from '@core/api/angular/operations/get-quick-search-results';
import {
  getState,
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  pipe,
  switchMap,
  tap,
} from 'rxjs';
import {
  QUICK_SEARCH_TYPE_LIST,
  QuickSearchResult,
} from './quick-search.model';

export enum QuickSearchStatus {
  INITIAL,
  LOADING,
  SUCCESS,
  ERROR,
}

export interface QuickSearchState {
  status: QuickSearchStatus;
  items: QuickSearchResult[];
  total: number;
  query: string;
  searchTypes: string[];
  end: boolean;
}

export const QUICK_SEARCH_PAGE_SIZE = 50;
export const QUICK_SEARCH_MIN_CHAR_LENGTH = 2;
export const QUICK_SEARCH_SCROLL_THRESHOLD = 200;

function stringifySearchTypes(searchTypes: string[]) {
  return JSON.stringify(
    Array.from(new Set(searchTypes).values()).sort((a, b) =>
      a.localeCompare(b),
    ),
  );
}

export const QuickSearchStore = signalStore(
  { providedIn: 'root' },
  withState<QuickSearchState>({
    status: QuickSearchStatus.INITIAL,
    items: [],
    total: 0,
    query: '',
    searchTypes: [],
    end: false,
  }),
  withProps(() => ({
    searchTypeList: inject(QUICK_SEARCH_TYPE_LIST),
  })),
  withComputed(({ status, items }) => ({
    isLoading: computed(() => status() === QuickSearchStatus.LOADING),
    loaded: computed(() => status() === QuickSearchStatus.SUCCESS),
    from: computed(() => items().length),
  })),
  withMethods((store, apiService = inject(ApiService)) => {
    const handleResult = (result: QuickSearchResponseDto) => {
      patchState(store, (state) => {
        const length = result.body?.hits?.hits?.length;
        const items =
          result.body?.hits.hits
            ?.map((hit) => hit._source)
            ?.filter((hit) => !!hit) ?? [];
        return {
          items: [...state.items, ...items],
          total: result.body?.hits.total?.value ?? 0,
          status: QuickSearchStatus.SUCCESS,
          end: !length || length < QUICK_SEARCH_PAGE_SIZE,
        };
      });
    };

    const handleError = (error: unknown) => {
      console.error(error);
      patchState(store, {
        items: [],
        status: QuickSearchStatus.ERROR,
        end: true,
      });
    };

    return {
      clearQuery: () => {
        patchState(store, () => ({
          query: '',
          status: QuickSearchStatus.INITIAL,
          cur: 0,
          items: [],
        }));
      },
      setQuery: rxMethod<string>(
        pipe(
          distinctUntilChanged(),
          tap((query) =>
            patchState(store, () => ({
              query,
            })),
          ),
        ),
      ),
      setSearchTypes: rxMethod<string[]>(
        pipe(
          distinctUntilChanged(),
          tap((searchTypes) =>
            patchState(store, () => ({
              searchTypes,
            })),
          ),
        ),
      ),
      loadMore: rxMethod<void>(
        pipe(
          filter(() => !getState(store).end),
          filter(() => getState(store).status !== QuickSearchStatus.LOADING),
          tap(() =>
            patchState(store, () => ({
              status: QuickSearchStatus.LOADING,
            })),
          ),
          switchMap(() => {
            const state = getState(store);
            return apiService
              .invoke(getQuickSearchResults, {
                searchFor: state.query,
                searchTypes: state.searchTypes,
                from: store.from(),
                size: QUICK_SEARCH_PAGE_SIZE,
              })
              .pipe(
                tap(handleResult),
                catchError((error) => {
                  handleError(error);
                  return of(null);
                }),
              );
          }),
        ),
      ),
      search: rxMethod<{
        query: string;
        searchTypes: string[];
      }>(
        pipe(
          debounceTime(500),
          distinctUntilChanged(
            (prev, cur) =>
              stringifySearchTypes(prev.searchTypes) ===
                stringifySearchTypes(cur.searchTypes) &&
              prev.query === cur.query,
          ),
          tap(({ query, searchTypes }) =>
            patchState(store, () => ({
              query,
              searchTypes,
              status: QuickSearchStatus.LOADING,
              items: [],
              end: false,
            })),
          ),
          filter(({ query }) => query.length > QUICK_SEARCH_MIN_CHAR_LENGTH),
          map(({ query, searchTypes }) => ({
            query: query.toLowerCase(),
            searchTypes,
          })),
          switchMap(({ query, searchTypes }) => {
            return apiService
              .invoke(getQuickSearchResults, {
                searchFor: query,
                searchTypes: searchTypes,
                from: 0,
                size: QUICK_SEARCH_PAGE_SIZE,
              })
              .pipe(
                tap(handleResult),
                catchError((error) => {
                  handleError(error);
                  return of(null);
                }),
              );
          }),
        ),
      ),
    };
  }),
  withHooks({
    onInit: (store) => {
      patchState(store, {
        searchTypes: store.searchTypeList.map((type) => type.value),
      });

      effect(() => {
        store.search({
          query: store.query(),
          searchTypes: store.searchTypes(),
        });
      });
    },
  }),
);
